import type { ToneDefinition } from "./models/index.js";
import { openAiSupportedLanguages } from "./openAiSupportedLanguages.js";

export type PartialToneDefinition = Pick<
  ToneDefinition,
  "title" | "language" | "positiveInstructions"
>;

/**
 * The following can be used in place of IDs when referencing tone definitions,
 * to point to example/builtin definitions
 */
export enum ExampleToneDefinitionId {
  ProfessionalAndAuthorative = "professional-authorative",
  FriendlyAndApproachable = "friendly-approachable",
  EnergeticAndPromotional = "energetic-promotional",
}

export const defaultLanguage = openAiSupportedLanguages[0];

export const exampleToneDefinitions: Record<
  ExampleToneDefinitionId,
  PartialToneDefinition
> = {
  [ExampleToneDefinitionId.ProfessionalAndAuthorative]: {
    title: "Professional & Authoritative",
    language: defaultLanguage,
    positiveInstructions: `
- Use clear, direct language
- Maintain a confident, knowledgeable tone
- Avoid contractions
- Prioritize clarity over flair
- Use formal vocabulary appropriate for business or industry settings
- Include evidence or examples when applicable`,
  },
  [ExampleToneDefinitionId.FriendlyAndApproachable]: {
    title: "Friendly & Approachable",
    language: defaultLanguage,
    positiveInstructions: `
- Use conversational language
- Employ contractions and light humor where appropriate
- Use inclusive and welcoming language (e.g., “Let’s,” “We,” “You’ll find…”)
- Keep sentences short and readable
- Use emojis or informal punctuation sparingly, if allowed`,
  },
  [ExampleToneDefinitionId.EnergeticAndPromotional]: {
    title: "Energetic & Promotional",
    language: defaultLanguage,
    positiveInstructions: `
- Use enthusiastic, dynamic wording
- Include calls to action (e.g., “Don’t miss out!” “Try it today!”)
- Use strong, positive adjectives and verbs
- Keep the pace quick with short, punchy sentences
- Emphasize benefits and unique selling points`,
  },
};

const mockCreatedAtDate = new Date();

/**
 * Returns an example tone definition by its ID, filling out missing fields with
 * mock values as necessary.
 */
export function getExampleToneDefinition(
  id: ExampleToneDefinitionId,
): ToneDefinition {
  const exampleToneDef = exampleToneDefinitions[id];

  return {
    ...exampleToneDef,
    organizationId: "",
    createdAt: mockCreatedAtDate,
    removedAt: null,
    negativeInstructions: null,
    id,
  };
}

/**
 * Narrows type if a given string is a valid example tone definition ID
 */
export function isExampleToneDefinitionId(
  maybeId: string | ExampleToneDefinitionId,
): maybeId is ExampleToneDefinitionId {
  return Object.values(ExampleToneDefinitionId).includes(
    maybeId as ExampleToneDefinitionId,
  );
}
